/**
 * EbmWorkspace — admin tool for the Ford EBM výsledovka (account→OZN mapping).
 *
 * Flow:
 *   1. Each month the admin uploads the accounting-software "výkaz" export
 *      (`POST /v1/management/ebm/vykaz`, multipart + rok/měsíc). The server
 *      persists it and returns the účet that aren't yet in the latest mapping.
 *   2. Those new účet render as a resolution table — one OZN input per row
 *      (account name pre-filled, editable) plus an „Abs" toggle for účet the
 *      report must show in Výnosy as |netto|. Saving them
 *      (`POST /v1/management/ebm/vysledovka`) creates the next full-snapshot
 *      version, so next month they're no longer "new".
 *   3. The current (or any past) version's mapping is shown below, switchable
 *      via the version history dropdown.
 *
 * Mirrors the SpzPairingWorkspace shell (Toolband + admin gate + table).
 */

import { useMemo, useRef, useState } from 'react';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Skeleton } from '@/components/Skeleton';
import { Toolband } from '@/components/Toolband';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { formatInt } from '@/lib/format';
import {
  useEbmBaselineUploadMutation,
  useEbmSaveMappingsMutation,
  useEbmVykazUploadMutation,
  useEbmVysledovkaQuery,
  type EbmTyp,
  type EbmVykazUploadResponse,
} from './api';

/**
 * Tooltip for the „Abs" column — same wording in both tables. In the výkaz the
 * flag also decides the column (Výnosy), which the rozvaha doesn't have.
 */
const ABS_HINT_VYKAZ =
  'Účet se vykáže vždy ve Výnosech, a to jako absolutní hodnota (|netto|) — bez ohledu na N/V v OZN.';
const ABS_HINT_ROZVAHA = 'Do reportu vstupuje absolutní hodnota (|netto|) místo netto.';

interface EbmWorkspaceProps {
  /** Company admin (spravce) or platform admin — mirrors the server gate. */
  canManage: boolean;
}

const EBM_TYP_OPTIONS: { value: EbmTyp; label: string }[] = [
  { value: 'vykaz', label: 'Výkaz' },
  { value: 'rozvaha_aktiva', label: 'Rozvaha — aktiva' },
  { value: 'rozvaha_pasiva', label: 'Rozvaha — pasiva' },
];
const EBM_TYP_LABEL: Record<EbmTyp, string> = {
  vykaz: 'Výkaz',
  rozvaha_aktiva: 'Rozvaha — aktiva',
  rozvaha_pasiva: 'Rozvaha — pasiva',
};

const MESICE = [
  'leden',
  'únor',
  'březen',
  'duben',
  'květen',
  'červen',
  'červenec',
  'srpen',
  'září',
  'říjen',
  'listopad',
  'prosinec',
];

interface Draft {
  nazev: string;
  ozn: string;
  /** Report sums |netto| for this účet — for účty exported with the opposite sign. */
  absolutni: boolean;
}

interface NameDraft {
  nazev: string;
  include: boolean;
}

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    return (err.body as { error?: string }).error ?? fallback;
  }
  return err instanceof Error ? err.message : fallback;
}

export function EbmWorkspace({ canManage }: EbmWorkspaceProps) {
  const now = new Date();
  // Which report type's mapping is being maintained.
  const [typ, setTyp] = useState<EbmTyp>('vykaz');
  const [rok, setRok] = useState(now.getFullYear());
  const [mesic, setMesic] = useState(now.getMonth() + 1);
  const [file, setFile] = useState<File | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // Baseline mapping upload (empty-state, per type).
  const [baselineFile, setBaselineFile] = useState<File | null>(null);
  const baselineInput = useRef<HTMLInputElement>(null);

  // Which version to display (undefined = latest).
  const [viewVerze, setViewVerze] = useState<number | undefined>(undefined);

  // Last upload's diff + the per-account drafts the admin is filling in:
  // `drafts` = OZN for new účet, `nameDrafts` = confirmable name backfills.
  const [result, setResult] = useState<EbmVykazUploadResponse | null>(null);
  const [drafts, setDrafts] = useState<Map<string, Draft>>(new Map());
  const [nameDrafts, setNameDrafts] = useState<Map<string, NameDraft>>(new Map());

  // Inline OZN + absolutní editing of the current výsledovka (latest version only).
  const [editMode, setEditMode] = useState(false);
  const [oznEdits, setOznEdits] = useState<Map<string, string>>(new Map());
  const [absEdits, setAbsEdits] = useState<Map<string, boolean>>(new Map());

  const vys = useEbmVysledovkaQuery(canManage, typ, viewVerze);
  const upload = useEbmVykazUploadMutation();
  const save = useEbmSaveMappingsMutation();
  const baseline = useEbmBaselineUploadMutation();

  // 'OZN' for the P&L výkaz, 'cílová buňka' for the balance-sheet rozvaha.
  const oznLabel = typ === 'vykaz' ? 'OZN' : 'Cílová buňka';
  const oznPlaceholder = typ === 'vykaz' ? 'např. B196 N' : 'např. L36';
  const absHint = typ === 'vykaz' ? ABS_HINT_VYKAZ : ABS_HINT_ROZVAHA;

  const switchTyp = (next: EbmTyp) => {
    if (next === typ) return;
    setTyp(next);
    setViewVerze(undefined);
    setResult(null);
    setDrafts(new Map());
    setNameDrafts(new Map());
    setEditMode(false);
    setOznEdits(new Map());
    setAbsEdits(new Map());
    setFile(null);
    setBaselineFile(null);
  };

  const runBaseline = () => {
    if (!baselineFile) {
      toast.warn('Vyberte soubor XLSX s výchozím mapováním.');
      return;
    }
    baseline.mutate(
      { file: baselineFile, typ },
      {
        onSuccess: (r) => {
          toast.ok(`Výchozí mapování nahráno — verze ${r.verze}, ${formatInt(r.pocet)} účtů.`);
          setBaselineFile(null);
        },
        onError: (err) => toast.error(errMessage(err, 'Nahrání výchozího mapování selhalo')),
      },
    );
  };

  const versions = vys.data?.versions ?? [];
  const shown = vys.data?.version ?? null;
  const rows = vys.data?.rows ?? [];

  const filledCount = useMemo(
    () => Array.from(drafts.values()).filter((d) => d.ozn.trim() !== '').length,
    [drafts],
  );
  const includedNameCount = useMemo(
    () => Array.from(nameDrafts.values()).filter((d) => d.include && d.nazev.trim() !== '').length,
    [nameDrafts],
  );

  // OZN editing applies to the newest version only (older versions are read-only history).
  const isLatest = shown != null && versions.length > 0 && shown.verze === versions[0].verze;
  const oznChanges = useMemo(() => {
    const out: { ucet: string; ozn: string }[] = [];
    for (const r of rows) {
      const edited = oznEdits.get(r.ucet);
      if (edited === undefined) continue;
      const v = edited.trim();
      if (v !== '' && v !== r.ozn) out.push({ ucet: r.ucet, ozn: v });
    }
    return out;
  }, [rows, oznEdits]);
  const absChanges = useMemo(() => {
    const out: { ucet: string; absolutni: boolean }[] = [];
    for (const r of rows) {
      const edited = absEdits.get(r.ucet);
      if (edited === undefined || edited === r.absolutni) continue;
      out.push({ ucet: r.ucet, absolutni: edited });
    }
    return out;
  }, [rows, absEdits]);
  const changedUcty = useMemo(
    () => new Set([...oznChanges, ...absChanges].map((c) => c.ucet)),
    [oznChanges, absChanges],
  );

  const runUpload = () => {
    if (!file) {
      toast.warn('Nejprve vyberte soubor výkazu.');
      return;
    }
    upload.mutate(
      { file, typ, rok, mesic },
      {
        onSuccess: (r) => {
          setResult(r);
          setDrafts(new Map(r.newAccounts.map((a) => [a.ucet, { nazev: a.nazev, ozn: '', absolutni: false }])));
          setNameDrafts(new Map(r.nameBackfills.map((a) => [a.ucet, { nazev: a.nazev, include: true }])));
          setViewVerze(undefined);
          const parts: string[] = [];
          if (r.newAccounts.length > 0) parts.push(`${formatInt(r.newAccounts.length)} nových účtů`);
          if (r.nameBackfills.length > 0) parts.push(`${formatInt(r.nameBackfills.length)} názvů k doplnění`);
          toast.ok(
            parts.length > 0
              ? `Výkaz nahrán. ${parts.join(' · ')}.`
              : `Výkaz nahrán. Žádné nové účty — všech ${formatInt(r.pocetUctu)} účtů je už zařazeno.`,
          );
        },
        onError: (err) => toast.error(errMessage(err, 'Nahrání výkazu selhalo')),
      },
    );
  };

  const setDraft = (ucet: string, patch: Partial<Draft>) => {
    setDrafts((prev) => {
      const next = new Map(prev);
      const cur = next.get(ucet) ?? { nazev: '', ozn: '', absolutni: false };
      next.set(ucet, { ...cur, ...patch });
      return next;
    });
  };

  const setNameDraft = (ucet: string, patch: Partial<NameDraft>) => {
    setNameDrafts((prev) => {
      const next = new Map(prev);
      const cur = next.get(ucet) ?? { nazev: '', include: true };
      next.set(ucet, { ...cur, ...patch });
      return next;
    });
  };

  const saveMappings = () => {
    if (!result) return;
    const mappings = result.newAccounts
      .map((a) => {
        const d = drafts.get(a.ucet);
        return {
          ucet: a.ucet,
          nazev: d?.nazev ?? a.nazev,
          ozn: (d?.ozn ?? '').trim(),
          absolutni: d?.absolutni ?? false,
        };
      })
      .filter((m) => m.ozn !== '');
    const nameUpdates = result.nameBackfills
      .map((b) => {
        const d = nameDrafts.get(b.ucet);
        return { ucet: b.ucet, nazev: (d?.nazev ?? b.nazev).trim(), include: d?.include ?? true };
      })
      .filter((n) => n.include && n.nazev !== '')
      .map((n) => ({ ucet: n.ucet, nazev: n.nazev }));
    if (mappings.length === 0 && nameUpdates.length === 0) {
      toast.warn('Vyplňte OZN u nového účtu nebo potvrďte doplnění názvu.');
      return;
    }
    save.mutate(
      { typ, importId: result.importId, mappings, nameUpdates },
      {
        onSuccess: (res) => {
          const parts: string[] = [];
          if (res.pridano > 0) parts.push(`přidáno ${formatInt(res.pridano)} účtů`);
          if (res.nazvyDoplneno > 0) parts.push(`doplněno ${formatInt(res.nazvyDoplneno)} názvů`);
          if (res.absZmeneno > 0) parts.push(`${formatInt(res.absZmeneno)}× absolutní hodnota`);
          toast.ok(`Vytvořena verze ${res.verze}${parts.length ? ` — ${parts.join(', ')}` : ''}.`);
          setResult(null);
          setDrafts(new Map());
          setNameDrafts(new Map());
          setViewVerze(undefined);
        },
        onError: (err) => toast.error(errMessage(err, 'Uložení selhalo')),
      },
    );
  };

  const setOznEdit = (ucet: string, ozn: string) =>
    setOznEdits((prev) => {
      const next = new Map(prev);
      next.set(ucet, ozn);
      return next;
    });

  const setAbsEdit = (ucet: string, absolutni: boolean) =>
    setAbsEdits((prev) => {
      const next = new Map(prev);
      next.set(ucet, absolutni);
      return next;
    });

  const cancelOznEdit = () => {
    setEditMode(false);
    setOznEdits(new Map());
    setAbsEdits(new Map());
  };

  const saveOznEdits = () => {
    if (oznChanges.length === 0 && absChanges.length === 0) {
      toast.warn('Žádné změny zařazení.');
      return;
    }
    save.mutate(
      { typ, oznUpdates: oznChanges, absUpdates: absChanges },
      {
        onSuccess: (res) => {
          const parts: string[] = [];
          if (res.oznZmeneno > 0) parts.push(`${formatInt(res.oznZmeneno)}× ${oznLabel}`);
          if (res.absZmeneno > 0) parts.push(`${formatInt(res.absZmeneno)}× absolutní hodnota`);
          toast.ok(`Vytvořena verze ${res.verze}${parts.length ? ` — upraveno ${parts.join(', ')}` : ''}.`);
          setEditMode(false);
          setOznEdits(new Map());
          setAbsEdits(new Map());
          setViewVerze(undefined);
        },
        onError: (err) => toast.error(errMessage(err, 'Uložení selhalo')),
      },
    );
  };

  if (!canManage) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-8 pb-8 pt-5">
        <EmptyState
          eyebrow="Pouze pro správce"
          title="EBM zařazení je dostupné pouze správcům firmy."
          description="Pokud potřebujete přístup, kontaktujte správce."
        />
      </div>
    );
  }

  const remaining = result ? result.newAccounts.length - filledCount : 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Toolband>
        <label className="flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Typ
          <select
            value={typ}
            onChange={(e) => switchTyp(e.target.value as EbmTyp)}
            className="border border-line bg-paper px-1.5 py-1 font-mono text-[11px] text-ink focus:border-accent focus:outline-none"
          >
            {EBM_TYP_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <input
          ref={fileInput}
          type="file"
          accept=".xml,text/xml,application/xml"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <Button
          variant="secondary"
          size="sm"
          icon={<Icon name="inbox" size={12} />}
          onClick={() => fileInput.current?.click()}
        >
          {file ? 'Změnit soubor' : 'Vybrat export (XML)'}
        </Button>
        {file && (
          <span className="max-w-[220px] truncate font-mono text-[10.5px] text-ink-2" title={file.name}>
            {file.name}
          </span>
        )}
        <label className="flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Období
          <select
            value={mesic}
            onChange={(e) => setMesic(Number(e.target.value))}
            className="border border-line bg-paper px-1.5 py-1 font-mono text-[11px] text-ink focus:border-accent focus:outline-none"
          >
            {MESICE.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
          <input
            type="number"
            value={rok}
            min={2000}
            max={2100}
            onChange={(e) => setRok(Number(e.target.value))}
            className="w-[68px] border border-line bg-paper px-1.5 py-1 font-mono text-[11px] tabular-nums text-ink focus:border-accent focus:outline-none"
          />
        </label>
        <Button
          variant="ink"
          size="sm"
          icon={<Icon name="upload" size={12} />}
          onClick={runUpload}
          disabled={!file || upload.isPending}
        >
          {upload.isPending ? 'Nahrávám…' : 'Nahrát měsíční export'}
        </Button>

        <Toolband.Spacer />

        {result && (
          <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
            {formatInt(result.pocetUctu)} účtů · {formatInt(result.matched)} zařazeno ·{' '}
            {formatInt(result.newAccounts.length)} nových
            {result.nameBackfills.length > 0 && <> · {formatInt(result.nameBackfills.length)} názvů</>}
          </span>
        )}
        {versions.length > 0 && (
          <label className="flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
            Verze
            <select
              value={viewVerze ?? ''}
              onChange={(e) => setViewVerze(e.target.value === '' ? undefined : Number(e.target.value))}
              disabled={editMode}
              className="border border-line bg-paper px-1.5 py-1 font-mono text-[11px] text-ink focus:border-accent focus:outline-none disabled:opacity-50"
            >
              <option value="">Nejnovější (v{versions[0].verze})</option>
              {versions.map((v) => (
                <option key={v.id} value={v.verze}>
                  v{v.verze} · {v.pocet} účtů · {v.vytvoreno.slice(0, 10)}
                </option>
              ))}
            </select>
          </label>
        )}
      </Toolband>

      <div
        data-testid="management-ebm"
        className="flex min-h-0 flex-1 flex-col gap-6 overflow-auto px-8 pb-8 pt-5"
      >
        {result && (result.newAccounts.length > 0 || result.nameBackfills.length > 0) && (
          <section className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-2">
                  K zařazení do nové verze
                </h3>
                <p className="mt-0.5 text-[12px] text-ink-3">
                  Přiřaďte OZN novým účtům a potvrďte doplnění chybějících názvů. Uložením vznikne nová
                  verze výsledovky.
                </p>
              </div>
              <Button
                variant="primary"
                size="sm"
                icon={<Icon name="check" size={12} />}
                onClick={saveMappings}
                disabled={save.isPending || (filledCount === 0 && includedNameCount === 0)}
              >
                {save.isPending
                  ? 'Ukládám…'
                  : `Uložit a vytvořit verzi (${formatInt(filledCount + includedNameCount)})`}
              </Button>
            </div>

            {result.newAccounts.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                    Nové účty — přiřaďte OZN
                  </h4>
                  {remaining > 0 && (
                    <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-warn">
                      {formatInt(remaining)} bez OZN — uloží se jen vyplněné
                    </span>
                  )}
                </div>
                <div className="overflow-x-auto border border-line bg-paper">
                  <table className="w-full border-collapse text-[12px]">
                    <thead className="bg-bg">
                      <tr className="text-left font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
                        <th className="w-[110px] px-3 py-2.5">Účet</th>
                        <th className="px-3 py-2.5">Název</th>
                        <th className="w-[180px] px-3 py-2.5">{oznLabel}</th>
                        <th className="w-[64px] px-3 py-2.5" title={absHint}>
                          Abs
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.newAccounts.map((a) => {
                        const d = drafts.get(a.ucet) ?? { nazev: a.nazev, ozn: '', absolutni: false };
                        return (
                          <tr key={a.ucet} data-testid="ebm-new-account" className="border-t border-line align-top">
                            <td className="px-3 py-2.5 font-mono text-[12px] font-semibold tabular-nums text-ink">
                              {a.ucet}
                            </td>
                            <td className="px-3 py-2">
                              <input
                                value={d.nazev}
                                onChange={(e) => setDraft(a.ucet, { nazev: e.target.value })}
                                className="w-full border border-line bg-paper px-2 py-1 text-[12px] text-ink focus:border-accent focus:outline-none"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                value={d.ozn}
                                onChange={(e) => setDraft(a.ucet, { ozn: e.target.value })}
                                placeholder={oznPlaceholder}
                                aria-label={`OZN pro účet ${a.ucet}`}
                                className="w-full border border-line bg-paper px-2 py-1 font-mono text-[12px] text-ink focus:border-accent focus:outline-none"
                              />
                            </td>
                            <td className="px-3 py-2.5">
                              <input
                                type="checkbox"
                                checked={d.absolutni}
                                onChange={(e) => setDraft(a.ucet, { absolutni: e.target.checked })}
                                aria-label={`Absolutní hodnota pro účet ${a.ucet}`}
                                title={absHint}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {result.nameBackfills.length > 0 && (
              <div className="flex flex-col gap-2">
                <h4 className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                  Doplnění názvů — účet je zařazen, ale bez názvu; výkaz jej má
                </h4>
                <div className="overflow-x-auto border border-line bg-paper">
                  <table className="w-full border-collapse text-[12px]">
                    <thead className="bg-bg">
                      <tr className="text-left font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
                        <th className="w-[64px] px-3 py-2.5">Doplnit</th>
                        <th className="w-[110px] px-3 py-2.5">Účet</th>
                        <th className="px-3 py-2.5">Název z výkazu</th>
                        <th className="w-[120px] px-3 py-2.5">{oznLabel}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.nameBackfills.map((b) => {
                        const d = nameDrafts.get(b.ucet) ?? { nazev: b.nazev, include: true };
                        const ozn = rows.find((r) => r.ucet === b.ucet)?.ozn ?? '';
                        return (
                          <tr key={b.ucet} data-testid="ebm-name-backfill" className="border-t border-line align-top">
                            <td className="px-3 py-2.5">
                              <input
                                type="checkbox"
                                checked={d.include}
                                onChange={(e) => setNameDraft(b.ucet, { include: e.target.checked })}
                                aria-label={`Doplnit název účtu ${b.ucet}`}
                              />
                            </td>
                            <td className="px-3 py-2.5 font-mono text-[12px] font-semibold tabular-nums text-ink">
                              {b.ucet}
                            </td>
                            <td className="px-3 py-2">
                              <input
                                value={d.nazev}
                                onChange={(e) => setNameDraft(b.ucet, { nazev: e.target.value })}
                                className="w-full border border-line bg-paper px-2 py-1 text-[12px] text-ink focus:border-accent focus:outline-none"
                              />
                            </td>
                            <td className="px-3 py-2 font-mono text-[12px] text-ink-2">{ozn}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        )}

        <section className="flex min-h-0 flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-2">
              Výsledovka
              {shown && (
                <span className="ml-2 normal-case tracking-normal text-ink-3">
                  verze {shown.verze} · {formatInt(shown.pocet)} účtů · {shown.vytvoreno.slice(0, 16)}
                  {shown.poznamka ? ` · ${shown.poznamka}` : ''}
                </span>
              )}
            </h3>
            {versions.length > 0 && isLatest ? (
              editMode ? (
                <div className="flex items-center gap-2">
                  <Button variant="secondary" size="sm" onClick={cancelOznEdit} disabled={save.isPending}>
                    Zrušit
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    icon={<Icon name="check" size={12} />}
                    onClick={saveOznEdits}
                    disabled={save.isPending || changedUcty.size === 0}
                  >
                    {save.isPending ? 'Ukládám…' : `Uložit změny (${formatInt(changedUcty.size)})`}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<Icon name="edit" size={12} />}
                  onClick={() => setEditMode(true)}
                >
                  Upravit zařazení
                </Button>
              )
            ) : versions.length > 0 ? (
              <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
                Historická verze — úpravy jen v nejnovější
              </span>
            ) : null}
          </div>

          {vys.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : vys.isError ? (
            <EmptyState
              eyebrow="Chyba"
              title="Načtení výsledovky selhalo."
              description={vys.error instanceof Error ? vys.error.message : 'Zkuste to znovu.'}
            />
          ) : versions.length === 0 ? (
            <div className="flex max-w-[620px] flex-col gap-3 border border-line bg-paper p-6">
              <div>
                <h4 className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-2">
                  Výchozí mapování — {EBM_TYP_LABEL[typ]}
                </h4>
                <p className="mt-1 text-[12px] leading-[1.5] text-ink-3">
                  Nahrajte výchozí soubor mapování (XML, příp. XLSX) — sloupce <b>A = účet</b>,{' '}
                  <b>B = {oznLabel}</b>, <b>C = název</b>. Vznikne verze 1; poté nahrávejte měsíční exporty (XML)
                  a doplňujte nové účty.
                </p>
              </div>
              <input
                ref={baselineInput}
                type="file"
                accept=".xml,.xlsx,text/xml,application/xml,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={(e) => setBaselineFile(e.target.files?.[0] ?? null)}
              />
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<Icon name="inbox" size={12} />}
                  onClick={() => baselineInput.current?.click()}
                >
                  {baselineFile ? 'Změnit soubor' : 'Vybrat soubor'}
                </Button>
                {baselineFile && (
                  <span className="max-w-[240px] truncate font-mono text-[10.5px] text-ink-2" title={baselineFile.name}>
                    {baselineFile.name}
                  </span>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  icon={<Icon name="upload" size={12} />}
                  onClick={runBaseline}
                  disabled={!baselineFile || baseline.isPending}
                >
                  {baseline.isPending ? 'Nahrávám…' : 'Nahrát výchozí mapování'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto border border-line bg-paper">
              <table className="w-full border-collapse text-[12px]">
                <thead className="bg-bg">
                  <tr className="text-left font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
                    <th className="w-[110px] px-3 py-2.5">Účet</th>
                    <th className="px-3 py-2.5">Název</th>
                    <th className="w-[160px] px-3 py-2.5">{oznLabel}</th>
                    <th className="w-[64px] px-3 py-2.5" title={absHint}>
                      Abs
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const editing = editMode && isLatest;
                    const changed = changedUcty.has(r.ucet);
                    return (
                      <tr
                        key={r.ucet}
                        className={
                          'border-t border-line ' + (changed ? 'bg-[color:var(--accent-soft)]' : '')
                        }
                      >
                        <td className="px-3 py-2 font-mono text-[12px] tabular-nums text-ink">{r.ucet}</td>
                        <td className="px-3 py-2 text-ink-2">
                          {r.nazev || <span className="text-ink-3">— bez názvu —</span>}
                        </td>
                        <td className="px-3 py-2">
                          {editing ? (
                            <input
                              value={oznEdits.get(r.ucet) ?? r.ozn}
                              onChange={(e) => setOznEdit(r.ucet, e.target.value)}
                              aria-label={`OZN pro účet ${r.ucet}`}
                              className="w-full border border-line bg-paper px-2 py-1 font-mono text-[12px] text-ink focus:border-accent focus:outline-none"
                            />
                          ) : (
                            <span className="font-mono text-[12px] font-semibold tracking-[0.04em] text-ink">
                              {r.ozn}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {editing ? (
                            <input
                              type="checkbox"
                              checked={absEdits.get(r.ucet) ?? r.absolutni}
                              onChange={(e) => setAbsEdit(r.ucet, e.target.checked)}
                              aria-label={`Absolutní hodnota pro účet ${r.ucet}`}
                              title={absHint}
                            />
                          ) : r.absolutni ? (
                            <span className="font-mono text-[11px] text-accent" title={absHint}>
                              abs
                            </span>
                          ) : (
                            <span className="text-ink-3">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
